export * from './auth'

import { API_BASE } from '../config'
import * as Sentry from '@sentry/nextjs'

/**
 * Wrapper fetch che cattura automaticamente gli errori API su Bugsink.
 * Lancia sempre un Error con il nome dell'endpoint e lo status HTTP.
 */
async function apiFetch(endpoint, url, options = {}, ignoredStatuses = []) {
  const res = await fetch(url, options)
  if (!res.ok) {
    let responseBody = null
    let parsedBody = null
    try {
      responseBody = await res.text()
      parsedBody = JSON.parse(responseBody)
    } catch {}
    const backendMessage = parsedBody?.error?.message
    const err = new Error(backendMessage || `${endpoint} fallito (${res.status})`)
    if (!ignoredStatuses.includes(res.status)) {
      Sentry.captureException(err, {
        tags: { api_endpoint: endpoint, http_status: res.status },
        extra: {
          url,
          method: options.method ?? 'GET',
          request_body: options.body ?? null,
          response_body: responseBody,
        },
      })
    }
    throw err
  }
  return res
}

export async function getBookingReport(token, date) {
  const url = `${API_BASE}/booking/report/${date}`
  const res = await apiFetch('booking/report', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function createBooking(token, booking) {
  const url = `${API_BASE}/booking/create`
  const res = await apiFetch('booking/create', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ booking }),
  })
  return res.json()
}

export async function createShopBooking(companyId, booking) {
  const url = `${API_BASE}/shop/booking/create?companyId=${encodeURIComponent(companyId)}`
  const res = await apiFetch('shop/booking/create', url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ booking }),
  }, [400])
  return res.json()
}

export async function createShopPaymentSession(companyId, bookingId) {
  const url = `${API_BASE}/shop/payment/create-session?companyId=${encodeURIComponent(companyId)}`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bookingId }),
  })
  if (!res.ok) {
    let message = `shop/payment/create-session fallito (${res.status})`
    try {
      const errData = await res.json()
      const backendMessage = errData?.error?.message || errData?.message
      if (backendMessage) message = backendMessage
    } catch {}
    const err = new Error(message)
    Sentry.captureException(err, {
      tags: { api_endpoint: 'shop/payment/create-session', http_status: res.status },
    })
    throw err
  }
  return res.json()
}

export async function modifyBooking(token, body) {
  const url = `${API_BASE}/shop/booking/modify/${encodeURIComponent(token)}`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = await res.json()
  if (!res.ok && res.status !== 400) {
    Sentry.captureException(new Error(`shop/booking/modify POST fallito (${res.status})`), {
      tags: { api_endpoint: 'shop/booking/modify POST', http_status: res.status },
      extra: { url, response_body: json },
    })
  }
  return json
}

export async function getBookingByModifyToken(token) {
  const url = `${API_BASE}/shop/booking/modify/${encodeURIComponent(token)}`
  const res = await fetch(url, { method: 'GET' })
  const json = await res.json()
  if (!res.ok && res.status !== 400) {
    Sentry.captureException(new Error(`shop/booking/modify fallito (${res.status})`), {
      tags: { api_endpoint: 'shop/booking/modify', http_status: res.status },
      extra: { url, response_body: json },
    })
  }
  return json
}

export async function getBookingBySession(sessionId, companyId) {
  const url = `${API_BASE}/shop/booking/session/${encodeURIComponent(sessionId)}?companyId=${encodeURIComponent(companyId)}`
  const res = await apiFetch('shop/booking/session', url, { method: 'GET' })
  return res.json()
}

export async function getBookingPdf(bookingId, companyId) {
  const url = `${API_BASE}/shop/booking/${encodeURIComponent(bookingId)}/pdf?companyId=${encodeURIComponent(companyId)}`
  const res = await apiFetch('shop/booking/pdf', url, { method: 'GET' })
  return res.blob()
}

export async function getUmbrellaAvailability(date) {
  const url = `${API_BASE}/shop/umbrella/availability/${date}?companyId=${process.env.NEXT_PUBLIC_COMPANY_ID}`
  const res = await apiFetch('shop/umbrella/availability', url, { method: 'GET' })
  return res.json()
}

export async function getUmbrellaAvailabilityAdmin(token, date) {
  const url = `${API_BASE}/umbrella/availability/${date}?companyId=${process.env.NEXT_PUBLIC_COMPANY_ID}`
  const res = await apiFetch('umbrella/availability', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function getExtraTypeAvailability(date) {
  const url = `${API_BASE}/shop/extra_type/availability/${date}?companyId=${process.env.NEXT_PUBLIC_COMPANY_ID}`
  const res = await apiFetch('shop/extra_type/availability', url, { method: 'GET' })
  return res.json()
}

export async function getExtraType(token, id) {
  const url = `${API_BASE}/extra_type/get/${id}`
  const res = await apiFetch('extra_type/get', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function deleteExtraType(token, id) {
  const url = `${API_BASE}/extra_type/delete/${id}`
  const res = await apiFetch('extra_type/delete', url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function updateExtraType(token, id, extraType) {
  const url = `${API_BASE}/extra_type/update/${id}`
  const res = await apiFetch('extra_type/update', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ extraType }),
  })
  return res.json()
}

export async function createExtraType(token, extraType) {
  const url = `${API_BASE}/extra_type/create`
  const res = await apiFetch('extra_type/create', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ extraType }),
  })
  return res.json()
}

export async function getCustomer(token, id) {
  const url = `${API_BASE}/customer/get/${id}`
  const res = await apiFetch('customer/get', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function deleteCustomer(token, id) {
  const url = `${API_BASE}/customer/delete/${id}`
  const res = await apiFetch('customer/delete', url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function updateCustomer(token, id, customer) {
  const url = `${API_BASE}/customer/update/${id}`
  const res = await apiFetch('customer/update', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ customer }),
  })
  return res.json()
}

export async function createCustomer(token, customer) {
  const url = `${API_BASE}/customer/create`
  const res = await apiFetch('customer/create', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ customer }),
  })
  return res.json()
}

export async function getCustomerList(token, params = {}) {
  const url = `${API_BASE}/customer/list`
  const res = await apiFetch('customer/list', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      pagination: true,
      pageNumber: 1,
      pageSize: 10,
      searchString: '',
      sortBy: '',
      sortDirection: '',
      ...params,
    }),
  })
  return res.json()
}

export async function exportCustomers(token) {
  const url = `${API_BASE}/customer/export`
  const res = await apiFetch('customer/export', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.blob()
}

export async function exportBookings(token, date) {
  const url = `${API_BASE}/booking/export/${date}`
  const res = await apiFetch('booking/export', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.blob()
}

export async function exportWeeklyBookings(token, date) {
  const url = `${API_BASE}/booking/weekly-export/${date}`
  const res = await apiFetch('booking/weekly-export', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.blob()
}

export async function getBooking(token, id) {
  const url = `${API_BASE}/booking/get/${id}`
  const res = await apiFetch('booking/get', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function updateBooking(token, id, booking) {
  const url = `${API_BASE}/booking/update/${id}`
  const res = await apiFetch('booking/update', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ booking }),
  })
  return res.json()
}

export async function deleteBooking(token, id) {
  const url = `${API_BASE}/booking/delete/${id}`
  const res = await apiFetch('booking/delete', url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function getBookingList(token, params = {}, date = null) {
  const query = date ? `?date=${date}` : ''
  const url = `${API_BASE}/booking/list${query}`
  const res = await apiFetch('booking/list', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      pagination: true,
      pageNumber: 1,
      pageSize: 10,
      searchString: '',
      sortBy: '',
      sortDirection: '',
      ...params,
    }),
  })
  return res.json()
}

export async function getFacilityClosureList(token) {
  const url = `${API_BASE}/facility_closure/list`
  const res = await apiFetch('facility_closure/list', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function deleteFacilityClosure(token, id) {
  const url = `${API_BASE}/facility_closure/delete/${id}`
  const res = await apiFetch('facility_closure/delete', url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function createFacilityClosure(token, facilityClosure) {
  const url = `${API_BASE}/facility_closure/create`
  const res = await apiFetch('facility_closure/create', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ facilityClosure }),
  })
  return res.json()
}

export async function getUmbrellaSectionList(token, sectionId, validFrom, validTo) {
  const url = `${API_BASE}/umbrella/section/price-list`
  const body = { section: sectionId, validFrom, validTo }
  const res = await apiFetch('umbrella/section/price-list', url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return res.json()
}

export async function getUmbrellaTypeList(token, params = {}) {
  const url = `${API_BASE}/umbrella/list`
  const body = {
    pagination: params.pagination ?? true,
    pageNumber: params.pageNumber ?? 0,
    pageSize: params.pageSize ?? 0,
    searchString: params.searchString ?? '',
    sortBy: params.sortBy ?? '',
    sortDirection: params.sortDirection ?? '',
  }
  const res = await apiFetch('umbrella/list', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  return res.json()
}

export async function bulkCreateUmbrellaPrice(token, { price, validFrom, validTo, section }) {
  const url = `${API_BASE}/umbrella/price/bulk-create`
  const res = await apiFetch('umbrella/price/bulk-create', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ umbrellaType: null, price, validFrom, validTo, section, row: null, seat: null }),
  })
  return res.json()
}

export async function toggleArrival(token, bookingId, umbrellaId) {
  const url = `${API_BASE}/booking/${encodeURIComponent(bookingId)}/umbrella/${encodeURIComponent(umbrellaId)}/toggle-arrival`
  const res = await apiFetch('booking/toggle-arrival', url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function getBookingConflicts(token) {
  const url = `${API_BASE}/diagnostics/booking-conflicts`
  const res = await apiFetch('diagnostics/booking-conflicts', url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  return res.json()
}

export async function getExtraTypeList(token, params = {}) {
  const url = `${API_BASE}/extra_type/list`
  const res = await apiFetch('extra_type/list', url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      pagination: false,
      pageNumber: 0,
      pageSize: 0,
      searchString: '',
      sortBy: '',
      sortDirection: '',
      ...params,
    }),
  })
  return res.json()
}
